import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Paper, Avatar, List, ListItem, ListItemText, ListItemAvatar, Divider, CircularProgress, Alert, Link as MuiLink, Stack, Button } from '@mui/material'; // Added Stack
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import BookIcon from '@mui/icons-material/Book';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import MenuBookIcon from '@mui/icons-material/MenuBook'; // Added for Benny's Journal
import LaunchIcon from '@mui/icons-material/Launch'; // Added for D&D Beyond link
import { useAuth } from '../context/AuthContext';
import { getCharactersByUserId } from '../services/characterService';
import { getRecentPosts } from '../services/postService';
import { getPlannedSchedule } from '../services/scheduleService';
import { getAllJournalEntries } from '../services/bennyJournalService';
import { getAllCampaignEpisodes } from '../services/campaignEpisodeService'; // Added
import { getAllAccolades } from '../services/accoladeService'; // Added for Trophy Hall
import { getMaps } from '../services/mapService'; // Added for Maps
import { getAllWikiEntries } from '../services/wikiService'; // Added for Wiki
import CampaignIcon from '@mui/icons-material/Campaign'; // Added for Campaign Episode section
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'; // Added for Trophy Hall
import MapIcon from '@mui/icons-material/Map'; // Added for Maps
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks'; // Added for Wiki

// Helper function to format class display
const formatCharacterClasses = (classes, totalLevel) => {
  if (!classes || classes.length === 0) return `Level ${totalLevel || 'N/A'}`;
  const classString = classes.map(c => `${c.className} ${c.classLevel}`).join(' / ');
  return `${classString} (Total Level ${totalLevel})`;
};

const Dashboard = () => {
  const { currentUser, firestoreUser } = useAuth();
  const navigate = useNavigate();
  const [currentCharacter, setCurrentCharacter] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true); // Main loading for characters and posts
  const [bestDay, setBestDay] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [scheduleError, setScheduleError] = useState('');

  // State for Benny's Journal Widget (now the keystone widget with multiple entries)
  const [recentJournalEntries, setRecentJournalEntries] = useState([]);
  const [loadingJournalEntries, setLoadingJournalEntries] = useState(true);
  const [journalEntriesError, setJournalEntriesError] = useState('');

  // State for Latest Campaign Episode Widget
  const [latestCampaignEpisode, setLatestCampaignEpisode] = useState(null);
  const [loadingCampaignEpisode, setLoadingCampaignEpisode] = useState(true);
  const [campaignEpisodeError, setCampaignEpisodeError] = useState('');

  // State for Trophy Hall Widget
  const [recentAccolades, setRecentAccolades] = useState([]);
  const [loadingAccolades, setLoadingAccolades] = useState(true);
  const [accoladesError, setAccoladesError] = useState('');

  // State for Maps Widget
  const [recentMaps, setRecentMaps] = useState([]);
  const [loadingMaps, setLoadingMaps] = useState(true);
  const [mapsError, setMapsError] = useState('');

  // State for Wiki Widget
  const [recentWikiEntries, setRecentWikiEntries] = useState([]);
  const [loadingWiki, setLoadingWiki] = useState(true);
  const [wikiError, setWikiError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      if (firestoreUser) {
        try {
          const chars = await getCharactersByUserId(firestoreUser.uid);
          if (chars.length > 0) {
            const activeChar = chars.find(c => c.isActive);
            setCurrentCharacter(activeChar || chars[0]);
          } else {
            setCurrentCharacter(null);
          }
          const posts = await getRecentPosts(3);
          setRecentPosts(posts);
        } catch (error) {
          console.error("Error fetching dashboard data:", error);
        }
      }
      setLoading(false); // Combined loading state
    };

    const fetchSchedule = async () => {
      setLoadingSchedule(true);
      setScheduleError('');
      try {
        const scheduleData = await getPlannedSchedule();
        if (scheduleData && scheduleData.bestCalculatedDay) {
          setBestDay(scheduleData.bestCalculatedDay);
        } else {
          setBestDay('Not yet determined');
        }
      } catch (err) {
        console.error("Error fetching schedule:", err);
        setScheduleError('Failed to load schedule. Please try again.');
        setBestDay('Error');
      } finally {
        setLoadingSchedule(false);
      }
    };

    const fetchRecentJournal = async () => {
      setLoadingJournalEntries(true);
      setJournalEntriesError('');
      try {
        const entries = await getAllJournalEntries();
        if (entries && entries.length > 0) {
          // Sort entries by createdAt in descending order (newest first)
          const sortedEntries = [...entries].sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
            return dateB - dateA;
          });
          // Get the 5 most recent entries for the keystone widget
          setRecentJournalEntries(sortedEntries.slice(0, 5));
        } else {
          setRecentJournalEntries([]);
        }
      } catch (err) {
        console.error("Error fetching recent journal entries:", err);
        setJournalEntriesError('Failed to load recent journal entries.');
      } finally {
        setLoadingJournalEntries(false);
      }
    };    const fetchCampaignData = async () => {
      setLoadingCampaignEpisode(true);
      setCampaignEpisodeError('');
      try {
        const episodes = await getAllCampaignEpisodes();

        if (episodes && episodes.length > 0) {
          // Sort episodes: by episodeNumber descending if available, otherwise by createdAt descending
          const sortedEpisodes = [...episodes].sort((a, b) => {
            if (a.episodeNumber && b.episodeNumber) {
              return b.episodeNumber - a.episodeNumber; // Higher episode number first
            }
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
            return dateB - dateA; // Newest first by date
          });
          const latestEpisode = sortedEpisodes[0];
          setLatestCampaignEpisode(latestEpisode);
        } else {
          setLatestCampaignEpisode(null);
        }
      } catch (err) {
        console.error("Error fetching latest campaign episode:", err);
        setCampaignEpisodeError('Failed to load latest campaign episode.');
      } finally {
        setLoadingCampaignEpisode(false);
      }
    };

    fetchData();
    fetchSchedule();
    fetchRecentJournal();
    fetchCampaignData();
    fetchTrophyHallData();
    fetchMapsData();
    fetchWikiData();
  }, [firestoreUser]);

  const fetchTrophyHallData = async () => {
    setLoadingAccolades(true);
    setAccoladesError('');
    try {
      const accolades = await getAllAccolades();
      if (accolades && accolades.length > 0) {
        // Sort by createdAt descending and take the 3 most recent
        const sortedAccolades = [...accolades].sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
          return dateB - dateA;
        });
        setRecentAccolades(sortedAccolades.slice(0, 3));
      } else {
        setRecentAccolades([]);
      }
    } catch (err) {
      console.error("Error fetching accolades:", err);
      setAccoladesError('Failed to load accolades.');
    } finally {
      setLoadingAccolades(false);
    }
  };

  const fetchMapsData = async () => {
    setLoadingMaps(true);
    setMapsError('');
    try {
      const maps = await getMaps();
      if (maps && maps.length > 0) {
        // Sort by createdAt descending and take the 3 most recent
        const sortedMaps = [...maps].sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
          return dateB - dateA;
        });
        setRecentMaps(sortedMaps.slice(0, 3));
      } else {
        setRecentMaps([]);
      }
    } catch (err) {
      console.error("Error fetching maps:", err);
      setMapsError('Failed to load maps.');
    } finally {
      setLoadingMaps(false);
    }
  };

  const fetchWikiData = async () => {
    setLoadingWiki(true);
    setWikiError('');
    try {
      const wikiEntries = await getAllWikiEntries();
      if (wikiEntries && wikiEntries.length > 0) {
        // Sort by updatedAt or createdAt descending and take the 5 most recent
        const sortedEntries = [...wikiEntries].sort((a, b) => {
          const dateA = a.updatedAt?.toDate ? a.updatedAt.toDate() : (a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0));
          const dateB = b.updatedAt?.toDate ? b.updatedAt.toDate() : (b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0));
          return dateB - dateA;
        });
        setRecentWikiEntries(sortedEntries.slice(0, 5));
      } else {
        setRecentWikiEntries([]);
      }
    } catch (err) {
      console.error("Error fetching wiki entries:", err);
      setWikiError('Failed to load wiki entries.');
    } finally {
      setLoadingWiki(false);
    }
  };

  // Combined loading state for initial dashboard render
  if (loading) { // Only use the main loading state for the initial full-screen loader
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', fontFamily: 'serif' }}>
        <CircularProgress sx={{ color: '#5C3D2E' }} />
        <Typography variant="h6" sx={{ ml: 2, color: '#5C3D2E' }}>Loading the Chronicles...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      flexGrow: 1,
      p: 3,
      backgroundColor: '#F5EFE6',
      minHeight: 'calc(100vh - 64px)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#5C3D2E', fontWeight: 'bold', textAlign: 'center', mb: 4, mt: 2 }}>
        Greetings, {currentUser?.email}!
      </Typography>

      {/* Benny's Journal Section - Keystone Widget */}
      {loadingJournalEntries ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', maxWidth: '1200px', mb: 3 }}>
          <CircularProgress sx={{ color: '#7A5C4E' }} />
          <Typography sx={{ ml: 2, color: '#7A5C4E' }}>Loading Journal Entries...</Typography>
        </Box>
      ) : journalEntriesError ? (
        <Alert severity="error" sx={{ width: '100%', maxWidth: '1200px', backgroundColor: '#FFEBEE', color: '#C62828', mb: 3 }}>
          {journalEntriesError}
        </Alert>
      ) : recentJournalEntries.length > 0 ? (
        <Paper
          elevation={5}
          sx={{
            p: 3,
            borderRadius: '12px',
            backgroundColor: '#E8DCCB',
            border: '2px solid #9a7a4a',
            boxShadow: '0px 6px 20px rgba(0,0,0,0.2)',
            mb: 4,
            width: '100%',
            maxWidth: '1000px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
            <Avatar sx={{ bgcolor: '#9a7a4a', mr: 2, width: 56, height: 56, border: '2px solid #7a5a2a' }}>
              <MenuBookIcon sx={{ color: '#F5EFE6', fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4E342E' }}>
                📜 Benny's Journal
              </Typography>
              <Typography variant="subtitle1" sx={{ color: '#6D4C41' }}>
                Recent Entries from the Chronicler
              </Typography>
            </Box>
          </Box>

          {/* List of Recent Journal Entries */}
          <Box sx={{ backgroundColor: '#F5EFE6', borderRadius: '8px', p: 2 }}>
            <List sx={{ width: '100%', bgcolor: 'transparent' }}>
              {recentJournalEntries.map((entry, index) => (
                <React.Fragment key={entry.id}>
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      py: 1.5,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#E8DCCB' },
                      borderRadius: '4px'
                    }}
                    onClick={() => {
                      if (entry.downloadURL) {
                        navigate(`/view-pdf/${entry.id}`, {
                          state: {
                            pdfUrl: entry.downloadURL,
                            journalTitle: entry.title,
                            audioURL: entry.audioURL,
                            audioFileName: entry.audioFileName,
                            from: '/dashboard'
                          }
                        });
                      } else {
                        navigate('/benny-journal');
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: '#9a7a4a', width: 40, height: 40 }}>
                        <MenuBookIcon fontSize="small" sx={{ color: '#F5EFE6' }} />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#5C3D2E', fontFamily: '"Georgia", serif' }}>
                          {entry.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          {entry.createdAt?.toDate && (
                            <Typography variant="caption" sx={{ color: '#7A5C4E', display: 'block', fontStyle: 'italic' }}>
                              Penned on: {entry.createdAt.toDate().toLocaleDateString()}
                            </Typography>
                          )}
                          {entry.audioURL && (
                            <Typography variant="caption" sx={{ color: '#8B7355', display: 'block', mt: 0.5 }}>
                              🎵 Audio Available
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                  {index < recentJournalEntries.length - 1 && <Divider variant="middle" component="li" sx={{ borderColor: '#C8BBAE' }} />}
                </React.Fragment>
              ))}
            </List>
          </Box>

          {/* View All Button */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={() => navigate('/benny-journal')}
              sx={{
                fontFamily: '"Georgia", serif',
                backgroundColor: '#9a7a4a',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#7a5a2a',
                },
              }}
            >
              View All Journal Entries
            </Button>
          </Box>
        </Paper>
      ) : (
        <Paper elevation={4} sx={{ p: 2.5, borderRadius: '8px', backgroundColor: '#E8DCCB', border: '2px solid #B08D57', mb: 3, width: '100%', maxWidth: '1000px', textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: '#5D4037' }}>No Journal Entries Yet</Typography>
          <Typography variant="body1" sx={{ color: '#7A5C4E', mt: 1 }}>
            The ink is dry; Benny has yet to chronicle the adventures!
          </Typography>
        </Paper>
      )}
      
      {/* Stack for the top widgets including Campaign Episode */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }} // Column on extra small, row on small and up
        spacing={3}
        sx={{ maxWidth: '1200px', width: '100%', justifyContent: 'center', mb: 3 }}
        alignItems="stretch" // Ensures Paper components stretch to the same height
      >
        {/* Current Character Section */}
        <Paper
          elevation={4}
          sx={{
            p: 2.5,
            borderRadius: '8px',
            backgroundColor: '#E8DCCB',
            border: '2px solid #B08D57',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            // height: '100%',
            cursor: 'pointer',
            '&:hover': {
              borderColor: '#7A5C4E',
              boxShadow: '0px 6px 16px rgba(0,0,0,0.25)',
            },
            overflowWrap: 'break-word',
            flexGrow: 1, // Allows the paper to grow
            width: { xs: '100%', sm: '33%' } // Full width on xs, 1/3 on sm and up
          }}
          onClick={() => navigate('/my-characters')}
        >
          {currentCharacter && currentCharacter.imageUrl ? (
            <Avatar
              src={currentCharacter.imageUrl}
              alt={currentCharacter.name}
              sx={{ width: 80, height: 80, mb: 2, border: '2px solid #5D4037' }}
            />
          ) : (
            <Avatar sx={{ bgcolor: '#6D4C41', width: 80, height: 80, mb: 2, border: '2px solid #5D4037' }}>
              <PersonIcon sx={{ fontSize: 50, color: '#F5EFE6' }} />
            </Avatar>
          )}
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#5D4037', mb: 1 }}>Current Character</Typography>
          {!firestoreUser ? (
            <Typography variant="body1" sx={{ color: '#7A5C4E', textAlign: 'center' }}>Loading user data...</Typography>          ) : currentCharacter ? (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ color: '#6D4C41', fontWeight: 'bold', mb: 0.5 }}>{currentCharacter.name}</Typography>
              <Typography variant="body1" sx={{ color: '#5D4037' }}>{formatCharacterClasses(currentCharacter.classes, currentCharacter.totalLevel)}</Typography>
              <Typography variant="body1" sx={{ color: '#5D4037' }}>Race: {currentCharacter.race}</Typography>
              {currentCharacter.dndBeyondLink && (
                <Button
                  variant="outlined"
                  startIcon={<LaunchIcon />}
                  href={currentCharacter.dndBeyondLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    mt: 1,
                    color: '#5D4037',
                    borderColor: '#5D4037',
                    fontSize: '0.75rem',
                    fontFamily: '"Georgia", serif',
                    '&:hover': {
                      backgroundColor: 'rgba(93, 64, 55, 0.1)',
                      borderColor: '#4A3A2A'
                    }
                  }}
                >
                  D&D Beyond
                </Button>
              )}
            </Box>
          ) : (
            <Typography variant="body1" sx={{ color: '#7A5C4E', textAlign: 'center' }}>
              No active character. <MuiLink component={RouterLink} to="/create-character" sx={{ color: '#7A5C4E', fontWeight: 'bold' }}>Create one?</MuiLink>
            </Typography>
          )}
        </Paper>

        {/* Best Day to Play Section */}
        <Paper
          elevation={4}
          sx={{
            p: 2.5,
            borderRadius: '8px',
            backgroundColor: '#E8DCCB',
            border: '2px solid #B08D57',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
            display: 'flex',
            flexDirection: 'column',
            // height: '100%',
            cursor: 'pointer',
            '&:hover': {
              borderColor: '#7A5C4E',
              boxShadow: '0px 6px 16px rgba(0,0,0,0.25)',
            },
            overflowWrap: 'break-word',
            flexGrow: 1,
            width: { xs: '100%', sm: '33%' }
          }}
          onClick={() => navigate('/campaign-schedule')}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar sx={{ bgcolor: '#6D4C41', mr: 2, border: '1px solid #5D4037' }}>
              <EventAvailableIcon sx={{ color: '#F5EFE6' }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#5D4037' }}>Best Day to Play</Typography>
          </Box>
          {loadingSchedule ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
              <CircularProgress sx={{ color: '#7A5C4E' }} />
            </Box>
          ) : scheduleError ? (
            <Alert severity="error" sx={{ width: '100%', backgroundColor: '#FFEBEE', color: '#C62828' }}>{scheduleError}</Alert>
          ) : bestDay ? (
            <Box sx={{ textAlign: 'center', my: 'auto' }}>
              <Typography variant="h4" sx={{ color: '#5C3D2E', fontWeight: 'bold' }}>
                {bestDay}
              </Typography>
              <Typography variant="caption" sx={{ color: '#7A5C4E', display: 'block', mt: 1 }}>
                Click to see full schedule details
              </Typography>
            </Box>
          ) : (
            <Typography variant="body1" sx={{ color: '#7A5C4E', textAlign: 'center', my: 'auto' }}>
              Schedule is being updated.
            </Typography>
          )}
        </Paper>

        {/* Latest Campaign Episode Widget */}
        <Paper
          elevation={4}
          sx={{
            p: 2.5,
            borderRadius: '8px',
            backgroundColor: '#E8DCCB',
            border: '2px solid #B08D57',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            '&:hover': {
              borderColor: '#7A5C4E',
              boxShadow: '0px 6px 16px rgba(0,0,0,0.25)',
            },
            overflowWrap: 'break-word',
            flexGrow: 1,
            width: { xs: '100%', sm: '33%' }
          }}
          onClick={() => latestCampaignEpisode ? navigate(`/campaign-episodes/${latestCampaignEpisode.id}`) : navigate('/campaign-episodes')}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar sx={{ bgcolor: '#795548', mr: 2, border: '1px solid #5D4037' }}>
              <CampaignIcon sx={{ color: '#F5EFE6' }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#5D4037' }}>Latest Episode</Typography>
          </Box>
          {loadingCampaignEpisode ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
              <CircularProgress sx={{ color: '#7A5C4E' }} />
            </Box>
          ) : campaignEpisodeError ? (
            <Alert severity="error" sx={{ width: '100%', backgroundColor: '#FFEBEE', color: '#C62828', fontSize: '0.75rem' }}>{campaignEpisodeError}</Alert>
          ) : latestCampaignEpisode ? (
            <Box sx={{ textAlign: 'center', my: 'auto', width: '100%' }}>
              <Typography variant="h6" sx={{ color: '#6D4C41', fontWeight: 'bold', mb: 0.5, wordWrap: 'break-word', fontSize: '1.1rem' }}>
                {latestCampaignEpisode.episodeNumber ? `Ep. ${latestCampaignEpisode.episodeNumber}: ` : ''}{latestCampaignEpisode.title}
              </Typography>
              {latestCampaignEpisode.createdAt?.toDate && (
                <Typography variant="caption" sx={{ color: '#7A5C4E', display: 'block', mt: 0.5 }}>
                  {latestCampaignEpisode.createdAt.toDate().toLocaleDateString()}
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: '#7A5C4E', display: 'block', mt: 1 }}>
                Click to view episode details
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#7A5C4E', textAlign: 'center', my: 'auto' }}>
              No episodes yet!
            </Typography>
          )}
        </Paper>
      </Stack>

      {/* Section: Trophy Hall, Maps, and Wiki - Three column layout */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={3}
        sx={{ maxWidth: '1200px', width: '100%', justifyContent: 'center', mt: 3 }}
        alignItems="stretch"
      >
        {/* Trophy Hall Widget */}
        <Paper
          elevation={4}
          sx={{
            p: 2.5,
            borderRadius: '8px',
            backgroundColor: '#E8DCCB',
            border: '2px solid #B08D57',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            '&:hover': {
              borderColor: '#7A5C4E',
              boxShadow: '0px 6px 16px rgba(0,0,0,0.25)',
            },
            flexGrow: 1,
            width: { xs: '100%', sm: '33%' }
          }}
          onClick={() => navigate('/trophy-hall')}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar sx={{ bgcolor: '#FFD700', mr: 2, border: '1px solid #5D4037' }}>
              <EmojiEventsIcon sx={{ color: '#5D4037' }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#5D4037' }}>Trophy Hall</Typography>
          </Box>
          {loadingAccolades ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
              <CircularProgress sx={{ color: '#7A5C4E' }} />
            </Box>
          ) : accoladesError ? (
            <Alert severity="error" sx={{ width: '100%', backgroundColor: '#FFEBEE', color: '#C62828' }}>{accoladesError}</Alert>
          ) : recentAccolades.length > 0 ? (
            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
              <List sx={{ width: '100%', bgcolor: 'transparent', py: 0 }}>
                {recentAccolades.map((accolade, index) => (
                  <React.Fragment key={accolade.id}>
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#FFD700', width: 32, height: 32 }}>
                          <EmojiEventsIcon fontSize="small" sx={{ color: '#5D4037' }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={<Typography variant="body2" sx={{ fontWeight: 'bold', color: '#5C3D2E', fontSize: '0.9rem' }}>{accolade.title}</Typography>}
                        secondary={<Typography variant="caption" sx={{ color: '#7A5C4E' }}>{accolade.characterName}</Typography>}
                      />
                    </ListItem>
                    {index < recentAccolades.length - 1 && <Divider variant="middle" sx={{ borderColor: '#C8BBAE' }} />}
                  </React.Fragment>
                ))}
              </List>
              <Typography variant="caption" sx={{ color: '#7A5C4E', display: 'block', mt: 1, textAlign: 'center' }}>
                Click to view all accolades
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#7A5C4E', textAlign: 'center', my: 'auto' }}>
              No accolades yet. Achieve greatness!
            </Typography>
          )}
        </Paper>

        {/* Maps Widget */}
        <Paper
          elevation={4}
          sx={{
            p: 2.5,
            borderRadius: '8px',
            backgroundColor: '#E8DCCB',
            border: '2px solid #B08D57',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            '&:hover': {
              borderColor: '#7A5C4E',
              boxShadow: '0px 6px 16px rgba(0,0,0,0.25)',
            },
            flexGrow: 1,
            width: { xs: '100%', sm: '33%' }
          }}
          onClick={() => navigate('/maps')}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar sx={{ bgcolor: '#8B7355', mr: 2, border: '1px solid #5D4037' }}>
              <MapIcon sx={{ color: '#F5EFE6' }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#5D4037' }}>Campaign Maps</Typography>
          </Box>
          {loadingMaps ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
              <CircularProgress sx={{ color: '#7A5C4E' }} />
            </Box>
          ) : mapsError ? (
            <Alert severity="error" sx={{ width: '100%', backgroundColor: '#FFEBEE', color: '#C62828' }}>{mapsError}</Alert>
          ) : recentMaps.length > 0 ? (
            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
              <List sx={{ width: '100%', bgcolor: 'transparent', py: 0 }}>
                {recentMaps.map((map, index) => (
                  <React.Fragment key={map.id}>
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#8B7355', width: 32, height: 32 }}>
                          <MapIcon fontSize="small" sx={{ color: '#F5EFE6' }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={<Typography variant="body2" sx={{ fontWeight: 'bold', color: '#5C3D2E', fontSize: '0.9rem' }}>{map.name}</Typography>}
                        secondary={<Typography variant="caption" sx={{ color: '#7A5C4E' }}>By {map.createdByName || 'Unknown'}</Typography>}
                      />
                    </ListItem>
                    {index < recentMaps.length - 1 && <Divider variant="middle" sx={{ borderColor: '#C8BBAE' }} />}
                  </React.Fragment>
                ))}
              </List>
              <Typography variant="caption" sx={{ color: '#7A5C4E', display: 'block', mt: 1, textAlign: 'center' }}>
                Click to view all maps
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#7A5C4E', textAlign: 'center', my: 'auto' }}>
              No maps yet. Time to chart the realm!
            </Typography>
          )}
        </Paper>

        {/* Wiki Widget */}
        <Paper
          elevation={4}
          sx={{
            p: 2.5,
            borderRadius: '8px',
            backgroundColor: '#E8DCCB',
            border: '2px solid #B08D57',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            '&:hover': {
              borderColor: '#7A5C4E',
              boxShadow: '0px 6px 16px rgba(0,0,0,0.25)',
            },
            flexGrow: 1,
            width: { xs: '100%', sm: '33%' }
          }}
          onClick={() => navigate('/wiki')}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar sx={{ bgcolor: '#7A5C4E', mr: 2, border: '1px solid #5D4037' }}>
              <LibraryBooksIcon sx={{ color: '#F5EFE6' }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#5D4037' }}>Wiki Updates</Typography>
          </Box>
          {loadingWiki ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
              <CircularProgress sx={{ color: '#7A5C4E' }} />
            </Box>
          ) : wikiError ? (
            <Alert severity="error" sx={{ width: '100%', backgroundColor: '#FFEBEE', color: '#C62828' }}>{wikiError}</Alert>
          ) : recentWikiEntries.length > 0 ? (
            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
              <List sx={{ width: '100%', bgcolor: 'transparent', py: 0 }}>
                {recentWikiEntries.map((entry, index) => (
                  <React.Fragment key={entry.id}>
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#7A5C4E', width: 32, height: 32 }}>
                          <LibraryBooksIcon fontSize="small" sx={{ color: '#F5EFE6' }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={<Typography variant="body2" sx={{ fontWeight: 'bold', color: '#5C3D2E', fontSize: '0.9rem' }}>{entry.title}</Typography>}
                        secondary={<Typography variant="caption" sx={{ color: '#7A5C4E' }}>{entry.type}</Typography>}
                      />
                    </ListItem>
                    {index < recentWikiEntries.length - 1 && <Divider variant="middle" sx={{ borderColor: '#C8BBAE' }} />}
                  </React.Fragment>
                ))}
              </List>
              <Typography variant="caption" sx={{ color: '#7A5C4E', display: 'block', mt: 1, textAlign: 'center' }}>
                Click to explore the wiki
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#7A5C4E', textAlign: 'center', my: 'auto' }}>
              No wiki entries yet. Start documenting!
            </Typography>
          )}
        </Paper>
      </Stack>

      {/* Most Recent Posts Section */}
      <Grid container spacing={3} sx={{ maxWidth: '1200px', width: '100%', justifyContent: 'center', mt: 3 }}>
        <Grid item xs={12}> {/* Adjusted to take full width */}
          <Paper
            elevation={4}
            sx={{
              p: 2.5,
              borderRadius: '8px',
              backgroundColor: '#E8DCCB',
              border: '2px solid #B08D57',
              boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: '#8D6E63', mr: 2, border: '1px solid #5D4037' }}>
                <BookIcon sx={{ color: '#F5EFE6' }} />
              </Avatar>
              <MuiLink component={RouterLink} to="/town-square" sx={{ textDecoration: 'none' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#5D4037', '&:hover': { color: '#795548' } }}>Most Recent Posts</Typography>
              </MuiLink>
            </Box>
            {recentPosts.length > 0 ? (
              <List sx={{ width: '100%', bgcolor: 'transparent' }}>
                {recentPosts.map((post, index) => (
                  <React.Fragment key={post.id}>
                    <ListItem
                      alignItems="flex-start"
                      sx={{
                        py: 1.5,
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#D7CCC8' },
                        borderRadius: '4px'
                      }}
                      component={RouterLink}
                      to={`/post/${post.id}`}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#A1887F', width: 36, height: 36 }}>
                          <BookIcon fontSize="small" sx={{ color: '#F5EFE6' }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={<Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#5C3D2E' }}>{post.title}</Typography>}
                        secondary={<Typography variant="body2" sx={{ color: '#7A5C4E' }}>{post.postType === 'inCharacter' && post.characterName ? post.characterName : post.userName}</Typography>}
                      />
                    </ListItem>
                    {index < recentPosts.length - 1 && <Divider variant="middle" component="li" sx={{ borderColor: '#C8BBAE'}} />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Typography variant="body1" sx={{ color: '#7A5C4E' }}>
                The scrolls are empty. No recent posts to display.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
